import { Button } from '@mui/material'
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import { PriceVision } from '../../../models/bid/PriceVision'
import { colorPrimary } from '../../../rsc/colors/colors'
import { isNullOrUndefined } from '../../../helper/helper'
import { PeriodType } from '../../business/MillBusiness'

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
    BarElement,
    Title,
    Tooltip,
    Legend
)

interface PriceGradingChartProps {
    priceVisions: PriceVision[]
    millId: number
    period: PeriodType
}

export const PriceGradingChart: React.FC<PriceGradingChartProps> = ({ priceVisions, millId, period }) => {

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartInstanceRef = useRef<ChartJS | null>(null)

    useEffect(() => {
        if (priceVisions && priceVisions.length > 0) {
            createChart()
        }

        // Cleanup on unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy()
            }
        }
    }, [priceVisions, period])

    const generateTimeRangeData = (): { labels: string[], dataMap: Map<string, PriceVision[]> } => {
        const now = dayjs()
        const labels: string[] = []
        const dataMap = new Map<string, PriceVision[]>()

        // Filter original data by mill if provided
        let filteredData = priceVisions.filter(vision => vision.market_day)

        switch (period) {
            case 'week':
                // Generate last 7 days
                for (let i = 6; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    // Find data for this specific date
                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'month':
                // Generate last 30 days
                for (let i = 29; i >= 0; i--) {
                    const date = now.subtract(i, 'days')
                    const dateKey = date.format('YYYY-MM-DD')
                    const label = date.format('MMM DD')
                    labels.push(label)

                    const dayData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM-DD') === dateKey
                    )
                    dataMap.set(label, dayData)
                }
                break

            case 'year':
                // Generate last 12 months
                for (let i = 11; i >= 0; i--) {
                    const date = now.subtract(i, 'months')
                    const monthKey = date.format('YYYY-MM')
                    const label = date.format('MMM YYYY')
                    labels.push(label)

                    const monthData = filteredData.filter(vision =>
                        dayjs(vision.market_day).utc().format('YYYY-MM') === monthKey
                    )
                    dataMap.set(label, monthData)
                }
                break
        }
        return { labels, dataMap }
    }

    const createChart = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy()
        }
        const { labels, dataMap } = generateTimeRangeData()
        const avgGrading = new Map<number, (number | null)[]>()
        const maxGrading = new Map<number, (number | null)[]>()
        labels.forEach((label, index) => {
            const dayData = dataMap.get(label) || []
            if (dayData.length === 0) {
                avgGrading.forEach((values, millId) => {
                    values[index] = null
                })
                maxGrading.forEach((values, millId) => {
                    values[index] = null
                })
            } else {
                dayData.forEach(vision => {
                    if (!millId) return
                    if (!avgGrading.has(millId)) {
                        avgGrading.set(millId, new Array(labels.length).fill(null))
                        maxGrading.set(millId, new Array(labels.length).fill(null))
                    }
                    const existingAvg = dayData.filter(g => !isNullOrUndefined(g.avgGradingByContract))
                    const existingMax = dayData.filter(g => !isNullOrUndefined(g.maxGrading))
                    const avg = existingAvg.reduce((acc, v) => acc + (v.avgGradingByContract ?? 0), 0) ?? null
                    const max = existingMax.reduce((acc, v) => acc + (v.maxGrading ?? 0), 0) ?? null
                    avgGrading.get(millId)![index] = avg > 0 ? Number((avg / existingAvg.length).toFixed(2)) : null
                    maxGrading.get(millId)![index] = max > 0 ? Number((max / existingMax.length).toFixed(2)) : null

                })
            }
        })
        const datasets: any[] = []
        const colors = [
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 205, 86)',
            'rgb(75, 192, 192)',
            'rgb(153, 102, 255)'
        ]
        Array.from(avgGrading.entries()).forEach(([millId, data], index) => {
            datasets.push({
                label: `avg grading`,
                data: data,
                borderColor: colors[3],
                backgroundColor: colors[3],
                tension: 0.4,
                spanGaps: true,
                yAxisID: 'y',
            })
        })
        Array.from(maxGrading.entries()).forEach(([millId, data], index) => {
            datasets.push({
                label: `max grading`,
                data: data,
                borderColor: colors[4],
                backgroundColor: colors[4],
                tension: 0.4,
                spanGaps: true,
                yAxisID: 'y1',
            })
        })
        const allValues = [
            ...datasets.find(ds => ds.yAxisID === 'y')?.data ?? [],
            ...datasets.find(ds => ds.yAxisID === 'y1')?.data ?? []
        ].filter(v => v !== null && v !== undefined);
        console.log('allValues', allValues)
        const minY = Math.min(...allValues, 0);
        const maxY = Math.max(...allValues, 1) + 1;
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        chartInstanceRef.current = new ChartJS(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets
            },
            options: {
                responsive: true,
                clip: false,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        min: minY,
                        max: maxY,
                        ticks: {
                            stepSize: 1
                        },
                        title: {
                            display: true,
                            text: t('grading') || ''
                        }
                    },
                                        y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        beginAtZero: true,
                        min: minY,
                        max: maxY,
                        ticks: {
                            stepSize: 1
                        },
                        title: {
                            display: true,
                            text: t('max_grading') || ''
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    x: {
                        title: {
                            display: true,
                            text: t('date') || ''
                        }
                    }
                },
            }
        })
    }

    const getPeriodButtonStyle = (buttonPeriod: PeriodType): React.CSSProperties => {
        return {
            padding: '8px 16px',
            margin: '2px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: period === buttonPeriod ? colorPrimary() : 'white',
            color: period === buttonPeriod ? 'white' : colorPrimary(),
            cursor: 'pointer',
            fontSize: '12px',
            height: '30px'
        }
    }

    return (
        <div style={{ width: '100%', marginTop: '20px' }}>
            <div className='pempem-flex-row-rev'>
                <div>
                    <h3 className='bold'>{t('chart_grading_title')}</h3>
                </div>
            </div>
            <div style={{ height: '400px' }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    )
}